import * as angular from 'angular';

import { ComponentDefinition, Identifier, Group, IdentifierType } from '../../models/index';
import { IidentifierService, ILoggerService, IStateService } from '../../services/';
import { groupRulesAddState, allRulesUpdated, oneRulesUpdated, groupRulesRemoveState } from '../../common/';
import { IRootScope } from 'IRootScope';

class IdentifierRulesController implements angular.IComponentController {
    deleteFromGroup: (stateGuid: string) => void;
    formController: angular.IFormController;
    group: Group;
    groups: Group[];
    identifierType = IdentifierType;
    isLoading: boolean;
    loadingMessage: string;
    rules: any[];
    selectedIdentifier: Identifier;
    selectedRule: any;

    private _garbages: (() => void)[] = [];

    static $inject = [
        '$rootScope',
        'identifierService',
        'loggerService',
        'stateService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _identifierService: IidentifierService,
        private _loggerService: ILoggerService,
        private _stateService: IStateService
    ) {
    }

    $onInit() {
        this._garbages.push(this._$rootScope.$on(groupRulesAddState, (event, data) => {
            if (this.selectedIdentifier) {
                this._stateService.getByGuid(data.itemId)
                    .then((state) => this._stateService.addToGroup(this.group.guid, state, this.selectedIdentifier.guid))
                    .then((state) => {
                        const rule = {
                            averageMax: 0,
                            averageMin: 0,
                            gradientMax: 0,
                            gradientMin: 0,
                            stdDevMax: 0,
                            stdDevMin: 0,
                            stateColor: state.state.markerColorString,
                            stateGuid: state.state.guid,
                            stateName: state.state.name
                        };
                        this.rules.push(rule);
                        this._loggerService.logSuccess('State added to group');
                    })
                    .catch(reason => {
                        this._loggerService.logError('Unable to add state to group');
                    });
            }
        }));

        this._garbages.push(this._$rootScope.$on(allRulesUpdated, () => {
            this.refresh();
        }));

        this._garbages.push(this._$rootScope.$on(oneRulesUpdated, () => {
            this.refresh();
        }));

        this.deleteFromGroup = (stateGuid) => {
            this._stateService.deleteFromGroup(stateGuid, this.group.guid, this.selectedIdentifier.guid)
                .then(() => {
                    this.rules = this.rules.filter(r => r.stateGuid !== stateGuid);
                    this._$rootScope.$broadcast(groupRulesRemoveState, stateGuid);
                    this._loggerService.logSuccess('State removed from group');
                }).catch(reason => {
                    this._loggerService.logError('Cannot remove state from group');
                });
        };
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj && changesObj['selectedIdentifier']) {
            this.refresh();
        }
    }

    private refresh() {
        if (this.selectedIdentifier) {
            this.isLoading = true;
            this._identifierService.getRules(this.selectedIdentifier.guid)
                .then(data => this.rules = data)
                .catch(reason => console.error(reason))
                .finally(() => this.isLoading = false);
        }
    }

    setClickedRow(index: number, $event: angular.IAngularEvent): void {
        this.selectedRule = this.rules[index];
    }

    save(): any {
        const result = this.validateForm();
        if (!result) {
            this.isLoading = true;
            this.loadingMessage = 'Updating';
            this._identifierService
                .updateRules(this.selectedIdentifier.guid, this.rules)
                .then(data => {
                    this._loggerService.logSuccess('Rules successfully updated!');
                    this.formController.$setPristine();
                })
                .catch(() => {
                    this._loggerService.logError('Failed to update rules.');
                }).finally(() => {
                    this.isLoading = false;
                });
        } else {
            return result;
        }
    }

    cancel(): void {
        this.formController.$setPristine();
    }

    private validateForm(): string {
        let validationResult = true;
        let result = '';
        this.rules.forEach(r => {
            validationResult = validationResult && this.validateValue(r, 'average');
            validationResult = validationResult && this.validateValue(r, 'stdDev');
            validationResult = validationResult && this.validateValue(r, 'gradient');
        });
        if (!validationResult) {
            result = 'Invalid';
        }
        return result;
    }

    private validateValue(r, valueName: string) {
        let result = true;
        let error = null;
        const valueMinName = valueName + 'Min';
        const valueMaxName = valueName + 'Max';
        if (r[valueMinName] > r[valueMaxName]) {
            error = 'MinMax';
            result = false;
        }
        this.formController.$setError(`${valueMinName}${r.stateGuid}`, error);
        this.formController.$setError(`${valueMaxName}${r.stateGuid}`, error);
        return result;
    }
}

export const wtIdentifierRules: ComponentDefinition = {
    componentId: 'wtIdentifierRules',
    controller: IdentifierRulesController,
    template: require('./identifierRules.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedIdentifier: '<',
        selectedRule: '=',
        group: '=',
        formController: '=',
        deleteFromGroup: '=',
        groups: '<'
    }
};
